/**
 * @module engine3d/dims3
 * @summary Dimensions placed by hand in the 3D view.
 * @description
 *   The automatic per-leg dimensions answer "how long is that pipe". These
 *   answer "how far is it from HERE to THERE" — between any two points worth
 *   measuring, with whatever text you want on them.
 *
 *   Both ends use the same anchor form as a pipe end (`Pipe3Node`), so a
 *   dimension pinned to a nozzle FOLLOWS that equipment when it is moved or
 *   raised, instead of quietly drifting off it. That is the whole reason they
 *   are stored as anchors rather than as two frozen points.
 *
 *   // INVARIANT: 3D-only. Nothing here is read by the 2D drawing.
 */
import type { Dim3, PortKey } from '../types';
import { S } from '../engine/state';
import type { Vec3 } from './math3';
import { resolveNode } from './pipes3';

/** Default distance the dimension line sits off the measured line, in pixels. */
export const DEFAULT_OFF = 30;

/**
 * A length in the units a person would say out loud.
 * Metres past a metre, millimetres below it — and never a trailing ".00", which
 * is noise on a drawing.
 */
export function formatLength(mm: number): string {
  if (!Number.isFinite(mm)) return '—';
  if (Math.abs(mm) >= 1000) {
    const m = mm / 1000;
    return `${m.toFixed(Math.abs(m) >= 10 ? 1 : 2).replace(/\.?0+$/, '')} m`;
  }
  return `${Math.round(mm)} mm`;
}

/** Both ends of a dimension in world space, or null if an anchor is gone. */
export function endsOfDim(d: Dim3): { a: Vec3; b: Vec3 } | null {
  const a = resolveNode(d.from);
  const b = resolveNode(d.to);
  if (!a || !b) return null;
  return { a: a.p, b: b.p };
}

/** Straight-line distance between a dimension's ends, in world units. */
export function measure(d: Dim3): number {
  const e = endsOfDim(d);
  if (!e) return 0;
  return Math.hypot(e.b.x - e.a.x, e.b.y - e.a.y, e.b.z - e.a.z);
}

/** What a dimension shows: your own text, or the measured length. */
export function textOf(d: Dim3, scaleMm: number): string {
  if (d.text !== undefined && d.text !== '') return d.text;
  return formatLength(measure(d) * scaleMm);
}

// ─── Leg dimensions ──────────────────────────────────────────

/**
 * The automatic per-leg figures are derived from geometry, so unlike a
 * hand-placed dimension there is no object to select. They are addressed by a
 * synthetic id instead — the run plus the leg's index within it — which is what
 * lets the selection, the caption and the length input all speak about the same
 * leg without inventing a document object for every straight of pipe.
 */
export const LEG_PREFIX = 'leg:';

export function legId(runId: string, seg: number): string {
  return `${LEG_PREFIX}${runId}:${seg}`;
}

export function isLegId(id: string): boolean {
  return id.startsWith(LEG_PREFIX) && /:\d+$/.test(id);
}

/** The run a leg id belongs to, or '' if it is not a leg id. */
export function legRun(id: string): string {
  if (!isLegId(id)) return '';
  return id.slice(LEG_PREFIX.length, id.lastIndexOf(':'));
}

/** The leg index in a leg id, or -1. */
export function legSeg(id: string): number {
  if (!isLegId(id)) return -1;
  return parseInt(id.slice(id.lastIndexOf(':') + 1), 10);
}

// ─── Editing ─────────────────────────────────────────────────────────────

let counter = 0;

/** Fresh id. A counter, not a clock, so headless runs stay deterministic. */
export function nextDimId(): string {
  counter++;
  let id = 'd3_' + counter;
  while (S.dims3.some((d) => d.id === id)) {
    counter++;
    id = 'd3_' + counter;
  }
  return id;
}

/** Keep the id counter clear of whatever a loaded document already uses. */
export function syncDimCounter(): void {
  for (const d of S.dims3) {
    const m = /^d3_(\d+)$/.exec(d.id);
    if (m) counter = Math.max(counter, parseInt(m[1], 10));
  }
}

export function findDim3(id: string): Dim3 | undefined {
  return S.dims3.find((d) => d.id === id);
}

export function isDim3Id(id: string): boolean {
  return S.dims3.some((d) => d.id === id);
}

export function deleteDim3(id: string): boolean {
  const i = S.dims3.findIndex((d) => d.id === id);
  if (i < 0) return false;
  S.dims3.splice(i, 1);
  return true;
}

/**
 * Both ends of a dimension, with whether each is pinned to geometry.
 *
 * An anchored end is shown too, not just free ones: you have to be able to grab
 * a dimension that was pinned to the wrong nozzle and move it. Grabbing one
 * DETACHES it (see `detachEnd`), which is what dragging it plainly means.
 */
export function editableEnds(d: Dim3): { index: 0 | 1; p: Vec3; anchored: boolean }[] {
  const e = endsOfDim(d);
  if (!e) return [];
  return [
    { index: 0, p: e.a, anchored: !d.from.p },
    { index: 1, p: e.b, anchored: !d.to.p },
  ];
}

/**
 * Turn an anchored end into a free point where it currently sits.
 * Called the moment its handle is grabbed, so dragging a pinned end works
 * without the drag first having to fight the anchor.
 */
export function detachEnd(d: Dim3, index: 0 | 1): boolean {
  const e = endsOfDim(d);
  if (!e) return false;
  const node = index === 0 ? d.from : d.to;
  if (node.p) return false;
  const at = index === 0 ? e.a : e.b;
  delete node.shapeId;
  delete node.port;
  delete node.pipeId;
  delete node.t;
  node.p = { x: Math.round(at.x * 10) / 10, y: Math.round(at.y * 10) / 10, z: Math.round(at.z * 10) / 10 };
  return true;
}

/** Pin an end back onto a piece of equipment. */
export function attachEnd(d: Dim3, index: 0 | 1, shapeId: string, port: PortKey): void {
  const node = index === 0 ? d.from : d.to;
  delete node.p;
  delete node.pipeId;
  delete node.t;
  node.shapeId = shapeId;
  node.port = port;
}

/** True when either end is pinned to geometry rather than floating free. */
export function isAnchored(d: Dim3): boolean {
  return !d.from.p || !d.to.p;
}

/** Move one end of a dimension. Ignored for an end pinned to geometry. */
export function moveDimEnd(d: Dim3, index: 0 | 1, to: Vec3): void {
  const r1 = (v: number) => Math.round(v * 10) / 10;
  const node = index === 0 ? d.from : d.to;
  if (!node.p) return;
  node.p = { x: r1(to.x), y: r1(to.y), z: r1(to.z) };
}

/** A short description for the HUD. */
export function describeDim3(d: Dim3, scaleMm: number): string {
  const label = d.text && d.text !== '' ? d.text : textOf(d, scaleMm);
  return `Dimension · ${label}`;
}
